import Barchart from 'components/molecules/Barchart';
import StackedBarChart from 'components/molecules/StackedBarChart';
import { promoCalenderId, TileData } from 'components/template/BumpReport/MainSection';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useRef } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
} from 'utils/helperMethods';

//chart spacing between each bars mapping with no of bars
export const sliderPaddingMapping: any = {
    12: 40,
    13: 12, //for promo comparison
    26: 12,
    52: 10,
};
const chartHeight = calculateContainerHeight(3);
const yAxisWidth = calculateFontSize(window.innerWidth, 60);
//calculates chart width and individual bar width
export const calculateChartWidth = (
    containerWidth: number,
    requiredBars: 12 | 26 | 52 | number,
    totalBars: number = 52,
) => {
    // 4000 / 23= 972
    // const totalWidthToBarWidthRatio =
    const updatedItemWidth = containerWidth / Math.min(requiredBars, totalBars);
    const updatedBarWidth = updatedItemWidth - sliderPaddingMapping[requiredBars];
    const updatedWidth = Math.ceil(totalBars * updatedItemWidth);
    return {
        updatedBarWidth: updatedBarWidth,
        updatedWidth,
    };
};

interface Props {
    sliderValue: number;
    chartConfig: any;
    setChartConfig: Function;
    tileData: TileData;
    chartData: any;
    optionSelected: any;
    timeBucketValue: number | null;
    isCalenderExpanded: boolean;
    showAllLabels: boolean;
    promoCalenderData: any;
}

const defaultBarLength = 52;
const BumpChartContainer: FC<Props> = ({
    optionSelected,
    chartData,
    sliderValue,
    chartConfig,
    setChartConfig,
    tileData,
    timeBucketValue,
    isCalenderExpanded,
    promoCalenderData,
    showAllLabels,
}) => {
    const chartRef = useRef<any>(null);
    const { appConfig } = useContext(UserContext);
    const bumpReportConfig = getConfig('bumpReport', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const tooltipKeyLabelMapping = bumpReportConfig.tooltipKeyLabelMapping;
    const tooltipExtraItems = bumpReportConfig.tooltipExtraItems;
    const isTableScrolling = useRef(false);
    const tooltipItemCache = useRef<any>({});
    const groupByKey = bumpReportConfig.groupByKey;
    const { data, loading, error, color } = chartData;
    const tileChartData = data ? data[tileData.absTile.key] : null;
    useEffect(() => {
        const { updatedBarWidth, updatedWidth } = calculateChartWidth(
            chartRef.current?.offsetWidth || chartConfig.width,
            sliderValue,
            timeBucketValue || defaultBarLength,
        );

        setChartConfig({ barWidth: updatedBarWidth, width: updatedWidth });
    }, [sliderValue, timeBucketValue]);

    useEffect(() => {
        // promoCalenderScrollElement?.scrollTo({
        //     left: chartConfig.width,
        //     behavior: 'smooth',
        // });
        chartRef.current?.scroll(chartConfig.width, 0);
    }, [chartConfig, tileData.bumpReportTile]);

    //listening to scroll
    useEffect(() => {
        const promoCalenderScrollElement = document
            .getElementById(promoCalenderId)
            ?.getElementsByClassName('ant-table-body')[0];
        //scroll table when user is scrolling bump chart
        function scrollFunction(event: any) {
            //if scrolling coming from table, then dont trigger graph scroll event listener
            //this is to avoid multiple graph scrolling
            if (isTableScrolling.current) return;
            //adding some gap here to avoid multiple function call
            setTimeout(() => {
                promoCalenderScrollElement?.scrollTo({
                    left: Number(chartRef.current?.scrollLeft) * 1, //giving a speed because widths of table and chart are different
                    behavior: 'auto',
                });
            }, 5);
        }
        chartRef.current?.addEventListener('scroll', scrollFunction, { passive: true });
        return () => {
            chartRef.current?.removeEventListener('scroll', scrollFunction);
        };
    }, [isCalenderExpanded, promoCalenderData]);

    //listening to table scroll and scrolling graph
    useEffect(() => {
        const promoCalenderScrollElement = document
            .getElementById(promoCalenderId)
            ?.getElementsByClassName('ant-table-body')[0];
        //scroll table when user is scrolling bump chart
        function scrollFunction(event: any) {
            setTimeout(() => {
                chartRef.current?.scrollTo({
                    left: Number(promoCalenderScrollElement?.scrollLeft) * 1, //giving a speed because widths of table and chart are different
                    behavior: 'auto',
                });
            }, 5);
            //adding some gap here to avoid multiple function call
            // setTimeout(() => {
            //     promoCalenderScrollElement?.scrollTo({
            //         left: Number(chartRef.current?.scrollLeft) * 1, //giving a speed because widths of table and chart are different
            //         behavior: 'auto',
            //     });
            // }, 5);
        }
        //doing this to avoid scroll event being called when scrolling graph
        function mouseEnterEventFunction() {
            isTableScrolling.current = true;
            promoCalenderScrollElement?.addEventListener('scroll', scrollFunction, { passive: true });
        }
        function mouseLeaveEventFunction() {
            isTableScrolling.current = false;
            promoCalenderScrollElement?.removeEventListener('scroll', scrollFunction);
        }
        promoCalenderScrollElement?.addEventListener('mouseenter', mouseEnterEventFunction);
        promoCalenderScrollElement?.addEventListener('mouseleave', mouseLeaveEventFunction);
        return () => {
            promoCalenderScrollElement?.removeEventListener('mouseenter', mouseEnterEventFunction);
            promoCalenderScrollElement?.removeEventListener('mouseleave', mouseLeaveEventFunction);
        };
    }, [isCalenderExpanded, promoCalenderData]);

    const currency =
        tileData?.volTile?.key === 'val' && tileData?.absTile.key === 'abs' ? fetchCurrency(appConfig) : '';
    return (
        <section>
            <div className="flex">
                {/* Y1 AXIS */}
                <div style={{ width: yAxisWidth + 80, paddingLeft: 80 }}>
                    <Barchart
                        // padding={[0, 0, 0, 48]}
                        padding={[0, 0, 17, 0]}
                        barWidth={chartConfig.barWidth}
                        xAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`bumpChart-y1`}
                        fields={['date', 'sales']}
                        //  minBound={scaleMinValue(40, tileChartData?.minVal)}
                        minBound={tileChartData?.minVal}
                        maxBound={
                            tileData?.absTile?.key === 'percentage'
                                ? tileChartData?.maxVal
                                : scaleMaxValue(40, tileChartData?.maxVal)
                        }
                        width={yAxisWidth + 80}
                        colorKey={'type'}
                        yAxisLabelFormatter={(val: any) => {
                            return tileData?.absTile?.key === 'percentage'
                                ? formatNumWithKMBAlt(val, decimalForPercentage)
                                : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        // Yline
                        YGrid={false}
                    />
                </div>
                {/* CHART COMPONENT */}
                <div
                    id="bump-chart-container"
                    ref={chartRef}
                    // style={{ width: chartConfig.width || 'auto' }}
                    className="overflow-x-auto w-full pb-[60px]"
                >
                    <StackedBarChart
                        totalKey={tooltipKeyLabelMapping[tileData.bumpReportTile.key]}
                        barWidth={chartConfig.barWidth}
                        // yAxisConfig={false}
                        lineChartParams={
                            tileData.bumpReportTile.key === groupByKey
                                ? null
                                : {
                                      lineColors: tileChartData?.lineColors?.map((item: any) => item?.color) || [],
                                      fields: tileChartData?.lineFields || [],
                                      minBound: tileChartData?.lineMinVal,
                                      maxBound: tileChartData?.lineMaxVal,
                                      color: colorConstants.darkBlueAlt,
                                      disableIntervalLabel: true,
                                      tooltipFormatter: (xAxis: any, val: any, from: any) => {
                                          let option: any = {};
                                          //checking if item exists in cache
                                          if (from in tooltipItemCache.current) option = tooltipItemCache.current[from];
                                          else {
                                              option = optionSelected?.filter((item: any) => {
                                                  return item?.value === from;
                                              })?.[0];
                                              if (option) tooltipItemCache.current[from] = option;
                                          }

                                          return {
                                              name: option ? option?.label : from,
                                              value:
                                                  (tileData.absTile.key === 'percentage' ? '' : option?.unit || '') +
                                                  formatNumWithKMBAlt(
                                                      val,
                                                      decimalForNonPercentage,
                                                      decimalForNonPercentage,
                                                  ),
                                          };
                                      },
                                  }
                        }
                        intervalFormatter={(val) => {
                            return tileData.absTile.key === 'percentage'
                                ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                : currency + formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        tooltipFormatter={(xAxis: any, val: any) => {
                            return {
                                name: xAxis,
                                value:
                                    tileData.absTile.key === 'percentage'
                                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                        : currency +
                                          formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                            };
                        }}
                        dataset={tileChartData?.data || []}
                        extras={{ data: tileChartData?.extras, dataMapping: tooltipExtraItems }}
                        height={chartHeight}
                        //  showLegend
                        loading={false}
                        chartContainer={`bumpChart-main`}
                        fields={['date', 'sales', 'type', 'value', 'index', 'areaData']}
                        areaChartParams={
                            tileData?.absTile?.key === 'percentage' || tileData.bumpReportTile.key === groupByKey
                                ? undefined
                                : {
                                      tooltipLabel: 'YA Sales',
                                      minBound: tileChartData?.minVal,
                                      maxBound:
                                          tileData?.absTile?.key === 'percentage'
                                              ? tileChartData?.maxVal
                                              : scaleMaxValue(40, tileChartData?.maxVal),
                                  }
                        }
                        //  minBound={scaleMinValue(40, tileChartData?.minVal)}
                        minBound={tileChartData?.minVal}
                        maxBound={
                            tileData?.absTile?.key === 'percentage'
                                ? tileChartData?.maxVal
                                : scaleMaxValue(40, tileChartData?.maxVal)
                        }
                        width={Math.floor(chartConfig.width) || window.innerWidth}
                        xAxisLabelFormatter={(val: any, item: any, index: number) => {
                            if (index % 4 === 0) return val;
                        }}
                        Xline={false}
                        skipIntervalLabel={showAllLabels ? 1 : 4}
                        colorKey={'type'}
                        intervalLabelKey="totalSales"
                        groupedChartColor={color}
                        currency={currency}
                    />
                </div>

                {/* Y2 AXIS */}
                <div style={{ width: yAxisWidth }}>
                    {tileData.bumpReportTile.key !== groupByKey ? (
                        <Barchart
                            padding={[0, 0, 19, 18]}
                            barWidth={chartConfig.barWidth}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`bumpChart-y2`}
                            fields={['date', 'value']}
                            minBound={tileChartData?.lineMinVal || 0}
                            maxBound={tileChartData?.lineMaxVal}
                            width={yAxisWidth}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            YGrid={false}
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default React.memo(BumpChartContainer);
